import { updateAgent } from '@/services/api';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import { AgentStatus, IAgent, IAgentData, LLM, LLM_OPTIONS, Presenter, StreamTextToSpeechVoice, Triggers, VideoType } from '@d-id/types';
import { AxiosError } from 'axios';
import { MutationFunction, useMutation } from 'react-query';
import { AGENT_DEFAULT_NAME } from '../constants';
import { getAgentBgObject } from '../utils';

export const useSaveDraftAgent = () => {
    const agentEditorStore = useAgentEditorStore();

    const saveDraftAgentMut: MutationFunction<IAgent | undefined, any> = async ({
        agentId,
        presenter,
        agentName,
        voiceProvider,
        greetings,
        presenterIsStitch,
        llm,
        triggers,
    }: {
        agentId?: string;
        presenter?: Presenter;
        agentName?: string;
        voiceProvider?: StreamTextToSpeechVoice;
        greetings?: string[];
        presenterIsStitch?: boolean;
        llm?: LLM;
        triggers: Triggers | undefined;
    }) => {
        if (!agentId) {
            return;
        }

        const presenterInfoAdapter: Record<VideoType, any> = {
            [VideoType.TALK]: {
                type: 'talk',
                source_url: agentEditorStore.presenterBackgroundSourceUrl || presenter?.poster,
                stitch: presenterIsStitch,
            },
            [VideoType.CLIP]: {
                type: 'clip',
                presenter_id: presenter?.id,
                driver_id: presenter?.driverId,
                ...getAgentBgObject(
                    VideoType.CLIP,
                    agentEditorStore.presenterBackgroundColor || presenter?.background?.color,
                    agentEditorStore.presenterBackgroundSourceUrl || presenter?.background?.source_url
                ),
            },
            [VideoType.Scene]: {},
            [VideoType.EXPRESSIVE]: {
                presenter_id: presenter?.id,
            },
            [VideoType.TRAIN]: {},
        };
        const commonPresenterInfo = {
            ...(voiceProvider && { voice: voiceProvider }),
            thumbnail: agentEditorStore.presenterBackgroundThumbnailUrl || presenter?.thumbnail || presenter?.poster,
        };
        const presenterType = (presenter?.type as VideoType) ?? 'talk';

        const formattedLLM = {
            ...llm,
            provider: llm?.provider ?? ('openai' as const),
            model: llm?.model ?? LLM_OPTIONS.GPT_4O_MINI,
        } as LLM;

        const agent: IAgentData = {
            preview_thumbnail: agentEditorStore.presenterBackgroundThumbnailUrl || presenter?.thumbnail || presenter?.poster,
            preview_name: agentName || AGENT_DEFAULT_NAME,
            presenter: {
                ...commonPresenterInfo,
                ...presenterInfoAdapter[presenterType],
            },
            llm: formattedLLM,
            ...(greetings && { greetings }),
            status: AgentStatus.Created,
            triggers,
        };
        const agentUpdates: { agentId: string; update: Partial<IAgentData> } = {
            agentId: agentId,
            update: agent,
        };

        return updateAgent(agentUpdates);
    };

    return useMutation('update-draft-agent', saveDraftAgentMut, {
        onError(error: AxiosError<{ kind: string; description?: string; details?: { terms: string[] } }>, requestData) {
            logger.error('error when update draft agent', { error, requestData });
        },
    });
};
